import React, { useState } from "react";
import { toast } from "sonner";
import { X, Save, Loader2, RotateCcw, CalendarClock, PoundSterling } from "lucide-react";
import { updatePricebookEntry, deletePricebookEntry } from "@/lib/api";

const todayISO = () => new Date().toISOString().slice(0, 10);

export const PriceBookEditModal = ({ entry, onClose, onSaved }) => {
  const [price, setPrice] = useState(String(entry?.current_price ?? ""));
  // Only prefill upcoming fields when a change is scheduled in the future.
  const scheduled = entry?.status === "scheduled";
  const [nextPrice, setNextPrice] = useState(
    scheduled ? String(entry?.upcoming_price ?? "") : ""
  );
  const [nextFrom, setNextFrom] = useState(
    scheduled ? String(entry?.effective_from ?? "").slice(0, 10) : ""
  );
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);

  const parsedPrice = Number(price);
  const parsedNextPrice = nextPrice === "" ? null : Number(nextPrice);
  const hasSchedule = nextPrice !== "" || nextFrom !== "";

  const save = async () => {
    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      return toast.error("Current price must be greater than £0");
    }
    if (hasSchedule) {
      if (!Number.isFinite(parsedNextPrice) || parsedNextPrice <= 0) {
        return toast.error("Upcoming price must be greater than £0");
      }
      if (!nextFrom) {
        return toast.error("Set the effective date for the upcoming price");
      }
      if (nextFrom < todayISO()) {
        return toast.error("Effective date must be today or later");
      }
    }
    setSaving(true);
    try {
      await updatePricebookEntry(entry.name, {
        price: parsedPrice,
        next_price: hasSchedule ? parsedNextPrice : null,
        next_effective_from: hasSchedule ? nextFrom : null,
      });
      toast.success(`Updated ${entry.name}.`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save price.");
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    if (!entry?.overridden) {
      return toast.info("This item is already on its code default — nothing to revert.");
    }
    setReverting(true);
    try {
      await deletePricebookEntry(entry.name);
      toast.success(`${entry.name} reverted to menu default.`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert.");
    } finally {
      setReverting(false);
    }
  };

  const clearSchedule = () => {
    setNextPrice("");
    setNextFrom("");
  };

  if (!entry) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-3 md:p-6 bg-black/60 overflow-y-auto"
      onClick={onClose}
      data-testid="pricebook-edit-modal"
    >
      <div
        className="brutal-card w-full max-w-xl bg-dotr-bg my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-dotr-ink p-4 md:p-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">
              {entry.category} · Edit price
            </div>
            <div className="font-heading font-semibold text-xl md:text-2xl uppercase tracking-tight">
              {entry.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="brutal-btn-ghost w-10 h-10 flex items-center justify-center"
            aria-label="Close"
            data-testid="btn-close-pricebook-modal"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 md:p-6 space-y-5">
          <div>
            <label className="label-uppercase flex items-center gap-1 mb-1.5">
              <PoundSterling size={12} />
              Current price
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="brutal-input h-11 px-3 font-body text-base w-full"
              data-testid="pricebook-edit-price"
              autoFocus
            />
            <span className="font-mono text-[11px] text-dotr-inkmuted">
              Applies immediately to all new orders.
            </span>
          </div>

          <div className="border-t-2 border-dotr-ink pt-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CalendarClock size={14} />
                <span className="label-uppercase !mb-0">Schedule a future change</span>
              </div>
              {hasSchedule && (
                <button
                  type="button"
                  onClick={clearSchedule}
                  className="font-mono text-[11px] uppercase tracking-widest text-dotr-inkmuted hover:text-dotr-primary"
                  data-testid="pricebook-clear-schedule"
                >
                  clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1.5 block">
                  New price
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={nextPrice}
                  onChange={(e) => setNextPrice(e.target.value)}
                  placeholder="£"
                  className="brutal-input h-11 px-3 font-body text-base w-full"
                  data-testid="pricebook-edit-next-price"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1.5 block">
                  Effective from
                </label>
                <input
                  type="date"
                  min={todayISO()}
                  value={nextFrom}
                  onChange={(e) => setNextFrom(e.target.value)}
                  className="brutal-input h-11 px-3 font-body text-base w-full"
                  data-testid="pricebook-edit-next-from"
                />
              </div>
            </div>
            <span className="font-mono text-[11px] text-dotr-inkmuted mt-1 block">
              Leave both fields empty for no scheduled change.
            </span>
          </div>
        </div>

        <div className="border-t-2 border-dotr-ink p-4 md:p-5 flex items-center justify-between flex-wrap gap-3">
          {entry.overridden ? (
            <button
              type="button"
              onClick={revert}
              disabled={reverting || saving}
              data-testid="pricebook-revert"
              className="brutal-btn-ghost px-4 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60"
              title="Delete the admin override and use the code default"
            >
              {reverting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Revert to default
            </button>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">
              Currently on code default
            </span>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving || reverting}
              className="brutal-btn-ghost px-4 py-2.5 text-xs"
              data-testid="pricebook-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || reverting}
              data-testid="pricebook-save"
              className="brutal-btn px-6 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-70"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceBookEditModal;
